import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Text, Box, Flex} from '@chakra-ui/react'
import DisplayPrice from '@salesforce/retail-react-app/app/components/display-price'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

/**
 * PDC Delivery Method or Return
 * @param {string} title - Title
 * @param {string} desc - Description
 * @param {string} price - Price
 * @returns {JSX.Element} - PDC Delivery Method or Return component
 */
export const PDCDeliveryMethodOrReturn = ({title, desc, price}) => {
    const intl = useIntl()
    const parsedPrice = parseFloat(price, 10)
    const priceData = {
        currentPrice: parsedPrice,
        // we don't want to show strikethrough price for cart since listPrice is not available via basket pricing
        listPrice: null,
        pricePerUnit: null,
        isASet: false,
        isMaster: false,
        isRange: false,
        isOnSale: false
    }
    const {currency} = useCurrency()

    /**
     * Renders the price information for a delivery method or return option.
     *
     * This function determines how the price should be displayed based on its value.
     * If the price is '0', it returns a localized message indicating that the service is free.
     * If a parsed price is available, it renders a `DisplayPrice` component with the appropriate
     * accessibility label, currency, and price data. Otherwise, it returns the price as a string.
     *
     * @returns {string|JSX.Element} - A string indicating 'FREE' if the price is zero, a JSX element
     *                                 for the `DisplayPrice` component if a parsed price is available,
     *                                 or the price as a string.
     */
    const renderPrice = () => {
        if (price.trim() === '0') {
            return intl.formatMessage({
                defaultMessage: 'FREE',
                id: 'pdc_cart_delivery_method_or_return.message.free'
            })
        } else if (parsedPrice) {
            return (
                <DisplayPrice
                    labelForA11y={title}
                    currency={currency}
                    priceData={priceData}
                    currentPriceProps={{
                        variant: 'bodyLarge1'
                    }}
                />
            )
        } else {
            return price
        }
    }

    return (
        <Flex justifyContent={'space-between'} mb="16px">
            <Box flex="1 0 80%">
                <Text variant={'bodyLarge2'}>{title}</Text>
                <Text variant={'bodySmall'} mt="4px" color="darkGray">
                    {desc}
                </Text>
            </Box>
            <Text as="span" flex="1 0 auto" variant={'bodyLarge1'} textAlign={'right'}>
                {renderPrice()}
            </Text>
        </Flex>
    )
}

PDCDeliveryMethodOrReturn.propTypes = {
    title: PropTypes.string,
    desc: PropTypes.string,
    price: PropTypes.string
}
